import { useEffect, useMemo, useState } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Link,
  useNavigate,
  useParams,
} from "react-router-dom";
import "./App.css";
import logoMainBlack from "./Images/logo-main-black.png";

const verificationApiUrl =
  process.env.REACT_APP_VERIFY_API_URL ||
  "https://us-central1-ubunifu-college.cloudfunctions.net/verifyDocument";

const navLinks = [
  { label: "Contact Us", href: "https://ubunifucollege.com/contact.html" },
];

const formatDate = (value) => {
  if (!value) return "—";
  if (typeof value === "string") {
    if (!Number.isNaN(Date.parse(value))) {
      return new Date(value).toLocaleDateString("en-KE", { dateStyle: "medium" });
    }
    return value;
  }
  if (value.toDate) return value.toDate().toLocaleDateString("en-KE", { dateStyle: "medium" });
  if (value.seconds) return new Date(value.seconds * 1000).toLocaleDateString("en-KE", { dateStyle: "medium" });
  if (value instanceof Date) return value.toLocaleDateString("en-KE", { dateStyle: "medium" });
  return "—";
};

const useDocumentTitle = (title) => {
  useEffect(() => {
    document.title = title;
  }, [title]);
};

const fetchVerificationResult = async ({ admissionNumber, type, signal }) => {
  const response = await fetch(verificationApiUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      admissionNumber,
      type,
    }),
    signal,
  });

  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(payload?.error?.message || "We could not reach the verification service.");
  }

  return payload;
};

const isCertificateValid = (student) => {
  if (!student) return false;
  const status = `${student.userStatus || student.Status || ""}`.trim().toLowerCase();
  return status === "completed";
};

const PageShell = ({ children }) => {
  return (
    <div className="app-shell">
      <header className="app-header">
        <div className="brand">
          <Link to="/" className="brand-link">
            <img src={logoMainBlack} alt="College logo" className="brand-logo" />
          </Link>
        </div>
        <nav className="app-nav">
          {navLinks.map((item) => (
            <a
              key={item.href}
              className="nav-link"
              href={item.href}
              target="_blank"
              rel="noreferrer"
            >
              {item.label}
            </a>
          ))}
        </nav>
      </header>
      <main className="app-main">{children}</main>
      <footer className="app-footer">
        <p>Verification results are served from our secure registry API.</p>
      </footer>
    </div>
  );
};

const HomePage = () => {
  useDocumentTitle("Ubunifu College");
  const [admissionNumber, setAdmissionNumber] = useState("");
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const handleNavigate = (type) => {
    const trimmed = admissionNumber.trim();
    if (!trimmed) {
      setError("Enter an admission number to continue.");
      return;
    }
    if (!/^[0-9]+$/.test(trimmed)) {
      setError("Admission number must be numbers only.");
      return;
    }
    if (trimmed.length < 6 || trimmed.length > 7) {
      setError("Admission number must be 6 to 7 digits.");
      return;
    }
    setError("");
    navigate(`/${type}/${trimmed}`);
  };

  return (
    <div className="home-grid">
      <section className="hero">
        <h1>Confirm certificates and student IDs in seconds.</h1>
        <p>
          Enter the admission number printed on the document. We verify directly against
          official records for fast, trusted results.
        </p>
        <div className="hero-note">
          Example links: <span>/certificates/123456</span> or <span>/school-id/123456</span>
        </div>
      </section>

      <section className="panel">
        <h2>Start a Verification</h2>
        <label className="field">
          <span>Admission Number</span>
          <input
            type="number"
            placeholder="e.g. 123456"
            value={admissionNumber}
            onChange={(event) => setAdmissionNumber(event.target.value)}
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={7}
            minLength={6}
          />
        </label>
        {error ? <p className="form-error">{error}</p> : null}
        <div className="action-row">
          <button className="primary" onClick={() => handleNavigate("certificates")}> 
            Verify Certificate
          </button>
          <button className="ghost" onClick={() => handleNavigate("school-id")}>
            Verify School ID
          </button>
        </div>
        <div className="panel-foot">
          <p>Need help? Contact admissions for verification support.</p>
        </div>
      </section>
    </div>
  );
};

const VerificationPage = ({ type }) => {
  const { admissionNumber } = useParams();
  const [loading, setLoading] = useState(true);
  const [student, setStudent] = useState(null);
  const [error, setError] = useState("");
  const rawStatus = student?.userStatus || student?.Status || "";

  const pageTitle =
    type === "certificate"
      ? `Certificate Verification • ${admissionNumber}`
      : `School ID Verification • ${admissionNumber}`;

  useDocumentTitle(pageTitle);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    setLoading(true);
    setError("");

    fetchVerificationResult({
      admissionNumber,
      type,
      signal: controller.signal,
    })
      .then((data) => {
        if (!active) return;
        setStudent(data?.student || null);
        if (!data?.found) {
          setError(data?.message || "No matching admission number found in the registry.");
        } else {
          setError("");
        }
      })
      .catch((error) => {
        if (!active) return;
        setStudent(null);
        setError(
          error instanceof Error && error.message
            ? error.message
            : "We could not reach the verification service. Try again shortly."
        );
      })
      .finally(() => {
        if (!active) return;
        setLoading(false);
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [admissionNumber, type]);

  const statusValue = useMemo(() => {
    if (!student) return "Not Found";
    if (type === "certificate") {
      return isCertificateValid(student) ? "Completed" : "Invalid Certificate";
    }
    return student.Status || student.userStatus || "Verified";
  }, [student, type]);

  const summaryCopy = useMemo(() => {
    if (!student) return "We could not find a record with that admission number.";
    if (type === "certificate") {
      return isCertificateValid(student)
        ? "Certificate record located in the official registry."
        : "";
    }
    return "Student ID record located in the official registry.";
  }, [student, type]);

  return (
    <div className="verify-layout">
      <section className="verify-header">
        <div>
          <p className="eyebrow">{type === "certificate" ? "Certificate" : "School ID"} Verification</p>
          <h1>Admission No. {admissionNumber}</h1>
          <p className="muted">{summaryCopy}</p>
        </div>
        <div
          className={`status-pill ${
            student ? (type === "certificate" && !isCertificateValid(student) ? "warn" : "ok") : "warn"
          }`}
        >
          {student ? (type === "certificate" && !isCertificateValid(student) ? "Invalid" : "Verified") : "Not Found"}
        </div>
      </section>

      <section className="verify-card">
        {loading ? (
          <div className="loading">
            <span className="loader" />
            <p>Looking up records...</p>
          </div>
        ) : student && type === "certificate" && !isCertificateValid(student) ? (
          <div className="empty-state">
            <h2>Certificate Invalid</h2>
            <p>{rawStatus ? `Current status: ${rawStatus}.` : ""}</p>
            <Link className="ghost link-btn" to="/">
              Try another search
            </Link>
          </div>
        ) : student ? (
          <div className="profile-grid">
            <div className="profile-summary">
              <div className="avatar">
                {student.profilePic ? (
                  <img src={student.profilePic} alt={student.bio || "Student"} />
                ) : (
                  <div className="avatar-fallback">UC</div>
                )}
              </div>
              <div>
                <h2>{student.bio || "Ubunifu Student"}</h2>
                <p className="muted">{student.course || "Course"}</p>
                <div className="chip-row">
                  <span className="chip">Admission #{student.admissionNumber}</span>
                  <span className="chip">Status: {statusValue}</span>
                </div>
              </div>
            </div>

            {type === "certificate" ? (
              <div className="detail-grid">
                <div>
                  <h3>Graduation</h3>
                  <p><span>Graduation Date</span>{formatDate(student.graduatedTime)}</p>
                </div>
              </div>
            ) : null}

          </div>
        ) : (
          <div className="empty-state">
            <h2>No Match Found</h2>
            <p>{error || "We couldn't locate a matching admission number."}</p>
            <Link className="ghost link-btn" to="/">
              Try another search
            </Link>
          </div>
        )}
      </section>

    </div>
  );
};

const NotFound = () => (
  <div className="empty-state">
    <h2>Page Not Found</h2>
    <p>That URL does not match a verification page.</p>
    <Link className="ghost link-btn" to="/">
      Back to verification
    </Link>
  </div>
);

function App() {
  return (
    <BrowserRouter>
      <PageShell>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/certificates/:admissionNumber" element={<VerificationPage type="certificate" />} />
          <Route path="/school-id/:admissionNumber" element={<VerificationPage type="school-id" />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </PageShell>
    </BrowserRouter>
  );
}

export default App;
